import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AnalyticsDataService, { DashboardData } from '../../../services/firebase/AnalyticsDataService';
import IncomeAllocationReport from './reports/IncomeAllocationReport';
import IncomeReport from './reports/IncomeReport';
import ReferralIncomeReport from './reports/ReferralIncomeReport';
import OutstandingBalancesReport from './reports/OutstandingBalancesReport';
import ClientInvoiceAgingReport from './reports/ClientInvoiceAgingReport';
import InvoicesReport from './reports/InvoicesReport';
import AppointmentStatusReport from './reports/AppointmentStatusReport';
import AttendanceReport from './reports/AttendanceReport';
import AppointmentRemindersReport from './reports/AppointmentRemindersReport';
import SentEmailsReport from './reports/SentEmailsReport';
import UnpaidInsuranceAppointmentsReport from './reports/UnpaidInsuranceAppointmentsReport';
import OutstandingClaimsReport from './reports/OutstandingClaimsReport';
import FiledClaimsReport from './reports/FiledClaimsReport';
import PaymentReportsReport from './reports/PaymentReportsReport';
import InsuranceStatusChecksReport from './reports/InsuranceStatusChecksReport';
import { useToast } from '../../../components/Toast';

/**
 * AnalyticsTab Component
 *
 * Comprehensive analytics and reporting interface for provider dashboard
 * Analytics dashboard design
 *
 * Features:
 * - Dashboard tab with financial overview
 * - Reports tab for detailed analytics
 * - Time period filters (This month, Last 30 days, Last month, This year, Custom)
 * - Income tracking with charts
 * - Outstanding balances breakdown (Clients vs Insurance)
 * - Appointments and Notes metrics with donut charts
 * - Projected statistics display
 * - Individual report pages for each report type
 *
 * TODO - Data Integration:
 * - Connect to real appointment data from AppointmentService
 * - Connect to real billing data from PatientService
 * - Calculate actual financial metrics from stored data
 * - Implement chart libraries (Chart.js or Recharts) for real visualizations
 * - Add date range filtering logic
 * - Implement custom date picker for "Custom" time period
 *
 * Current Status: UI shell complete with mock data
 */

type TimePeriod = 'this-month' | 'last-30-days' | 'last-month' | 'this-year' | 'custom';
type MainTab = 'dashboard' | 'reports';
type ReportView = 'main' | 'income-allocation' | 'income' | 'referral-income' | 'annual-2023' | 'annual-2024' | 'outstanding-balances' | 'client-invoice-aging' | 'invoices' | 'client-details' | 'client-demographics' | 'appointment-status' | 'attendance' | 'appointment-reminders' | 'sent-emails' | 'unpaid-insurance' | 'outstanding-claims' | 'filed-claims' | 'payment-reports' | 'insurance-status-checks';

const AnalyticsTab: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [mainTab, setMainTab] = useState<MainTab>('dashboard');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('this-month');
  const [currentReport, setCurrentReport] = useState<ReportView>('main');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);

  // Fetch real data from Firestore
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const analyticsService = AnalyticsDataService.getInstance();
    analyticsService.getDashboardData(user.uid, timePeriod)
      .then(data => {
        setDashboardData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('[AnalyticsTab] Failed to load data:', err);
        setLoading(false);
      });
  }, [user?.uid, timePeriod]);

  // Financial data not yet wired (no billing collections)
  const mockFinancials = {
    income: 0,
    outstandingBalances: {
      total: 0,
      clients: { total: 0, unpaid: 0, uninvoiced: 0 },
      insurance: { total: 0, unpaid: 0, unbilled: 0 }
    }
  };

  // If viewing a specific report, show that report component
  if (currentReport === 'income-allocation') {
    return <IncomeAllocationReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'income') {
    return <IncomeReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'referral-income') {
    return <ReferralIncomeReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'outstanding-balances') {
    return <OutstandingBalancesReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'client-invoice-aging') {
    return <ClientInvoiceAgingReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'invoices') {
    return <InvoicesReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'appointment-status') {
    return <AppointmentStatusReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'attendance') {
    return <AttendanceReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'appointment-reminders') {
    return <AppointmentRemindersReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'sent-emails') {
    return <SentEmailsReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'unpaid-insurance') {
    return <UnpaidInsuranceAppointmentsReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'outstanding-claims') {
    return <OutstandingClaimsReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'filed-claims') {
    return <FiledClaimsReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'payment-reports') {
    return <PaymentReportsReport onBack={() => setCurrentReport('main')} />;
  }
  if (currentReport === 'insurance-status-checks') {
    return <InsuranceStatusChecksReport onBack={() => setCurrentReport('main')} />;
  }

  return (
    <div style={{
      backgroundColor: '#28527A',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      border: '1px solid #6CA6C1'
    }}>
      {/* Page Title */}
      <h2 style={{
        fontSize: '1.875rem',
        fontWeight: '700',
        color: '#F7E9A0',
        marginBottom: '1.5rem',
        margin: 0
      }}>
        Analytics
      </h2>

      {/* Main Tabs: Dashboard | Reports */}
      <div style={{
        display: 'flex',
        gap: '2rem',
        borderBottom: '2px solid #3b6690',
        marginBottom: '1.5rem',
        marginTop: '1.5rem'
      }}>
        <button
          onClick={() => setMainTab('dashboard')}
          style={{
            background: 'none',
            border: 'none',
            padding: '0.75rem 0',
            fontSize: '1rem',
            fontWeight: mainTab === 'dashboard' ? '600' : '400',
            color: mainTab === 'dashboard' ? '#4A90E2' : '#9ca3af',
            cursor: 'pointer',
            borderBottom: mainTab === 'dashboard' ? '3px solid #4A90E2' : '3px solid transparent',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          Dashboard
        </button>
        <button
          onClick={() => setMainTab('reports')}
          style={{
            background: 'none',
            border: 'none',
            padding: '0.75rem 0',
            fontSize: '1rem',
            fontWeight: mainTab === 'reports' ? '600' : '400',
            color: mainTab === 'reports' ? '#4A90E2' : '#9ca3af',
            cursor: 'pointer',
            borderBottom: mainTab === 'reports' ? '3px solid #4A90E2' : '3px solid transparent',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          Reports
        </button>
      </div>

      {/* Dashboard Tab Content */}
      {mainTab === 'dashboard' && (
        <div>
          {/* Time Period Filters */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            {(['this-month', 'last-30-days', 'last-month', 'this-year', 'custom'] as TimePeriod[]).map((period) => (
              <button
                key={period}
                onClick={() => setTimePeriod(period)}
                style={{
                  background: timePeriod === period ? '#4A90E2' : '#3b6690',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: timePeriod === period ? '600' : '400'
                }}
                onMouseOver={(e) => {
                  if (timePeriod !== period) {
                    e.currentTarget.style.backgroundColor = '#4a7396';
                  }
                }}
                onMouseOut={(e) => {
                  if (timePeriod !== period) {
                    e.currentTarget.style.backgroundColor = '#3b6690';
                  }
                }}
              >
                {period === 'this-month' && 'This month'}
                {period === 'last-30-days' && 'Last 30 days'}
                {period === 'last-month' && 'Last month'}
                {period === 'this-year' && 'This year'}
                {period === 'custom' && 'Custom'}
              </button>
            ))}
          </div>

          {/* Projected Stats */}
          <div style={{
            backgroundColor: '#e8f4f8',
            borderRadius: '6px',
            padding: '0.75rem 1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '1rem'
          }}>
            <span style={{
              fontSize: '0.875rem',
              color: '#374151',
              fontWeight: '500'
            }}>
              Projected:
            </span>
            <span style={{
              fontSize: '0.875rem',
              color: '#374151'
            }}>
              | {dashboardData?.projected.appointments ?? 0} appointments
            </span>
            <span style={{
              fontSize: '0.875rem',
              color: '#374151'
            }}>
              • {dashboardData?.projected.clients ?? 0} clients
            </span>
          </div>

          {/* Main Dashboard Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            {/* Income Section */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem',
              minHeight: '350px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: 0
                }}>
                  Income
                </h3>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}>
                  View report
                </button>
              </div>

              {/* Income Amount */}
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1rem'
              }}>
                --
              </div>

              {/* Chart Area */}
              <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '2rem',
                minHeight: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#d1d5db',
                fontSize: '0.875rem'
              }}>
                No data
              </div>

              {/* Y-axis label (Dollars) */}
              <div style={{
                marginTop: '1rem',
                fontSize: '0.75rem',
                color: '#d1d5db'
              }}>
                Dollars
              </div>
            </div>

            {/* Outstanding Balances Section */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem',
              minHeight: '350px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: 0
                  }}>
                    Outstanding balances
                  </h3>
                  <span style={{
                    fontSize: '0.875rem',
                    color: '#d1d5db',
                    cursor: 'help'
                  }}>
                    ⓘ
                  </span>
                </div>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}>
                  View report
                </button>
              </div>

              {/* Total Outstanding Balance */}
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1.5rem'
              }}>
                ${mockFinancials.outstandingBalances.total.toLocaleString()}
              </div>

              {/* Clients Breakdown */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Clients
                  </span>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    ${mockFinancials.outstandingBalances.clients.total.toLocaleString()}
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#fecaca',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f87171'
                  }} />
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#f87171',
                      borderRadius: '2px',
                      display: 'inline-block'
                    }} />
                    <span style={{ color: '#d1d5db' }}>Unpaid</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#fecaca',
                      borderRadius: '2px',
                      display: 'inline-block'
                    }} />
                    <span style={{ color: '#d1d5db' }}>Uninvoiced</span>
                  </div>
                </div>
              </div>

              {/* Insurance Breakdown */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Insurance
                  </span>
                  <span style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    ${mockFinancials.outstandingBalances.insurance.total.toLocaleString()}
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#fecaca',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f87171'
                  }} />
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#f87171',
                      borderRadius: '2px',
                      display: 'inline-block'
                    }} />
                    <span style={{ color: '#d1d5db' }}>Unpaid</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: '#fecaca',
                      borderRadius: '2px',
                      display: 'inline-block'
                    }} />
                    <span style={{ color: '#d1d5db' }}>Unbilled</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Appointments Section */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem',
              minHeight: '350px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: 0
                }}>
                  Appointments
                </h3>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}>
                  View report
                </button>
              </div>

              {/* Appointments Count */}
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1rem'
              }}>
                {loading ? '...' : (dashboardData?.appointments.total ?? 0)}
              </div>

              {/* Appointment Breakdown */}
              {dashboardData && dashboardData.appointments.total > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { label: 'Completed', value: dashboardData.appointments.completed, color: '#10B981' },
                    { label: 'Scheduled', value: dashboardData.appointments.scheduled, color: '#3B82F6' },
                    { label: 'Confirmed', value: dashboardData.appointments.confirmed, color: '#6366F1' },
                    { label: 'Cancelled', value: dashboardData.appointments.cancelled, color: '#EF4444' },
                    { label: 'No-show', value: dashboardData.appointments.noShow, color: '#F59E0B' },
                  ].filter(item => item.value > 0).map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '100%', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '0.8rem', color: '#374151' }}>{item.label}</span>
                          <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>{item.value}</span>
                        </div>
                        <div style={{ width: '100%', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px' }}>
                          <div style={{
                            width: `${(item.value / dashboardData.appointments.total) * 100}%`,
                            height: '100%',
                            backgroundColor: item.color,
                            borderRadius: '3px',
                          }} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#6B7280' }}>
                    <span>Video: {dashboardData.appointments.byModality.video}</span>
                    <span>In-person: {dashboardData.appointments.byModality.inPerson}</span>
                  </div>
                </div>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minHeight: '200px', color: '#9CA3AF', fontSize: '0.875rem'
                }}>
                  {loading ? 'Loading...' : 'No appointments this period'}
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem',
              minHeight: '350px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: 0
                }}>
                  Notes
                </h3>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'none'
                }}>
                  View report
                </button>
              </div>

              {/* Notes Count */}
              <div style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '1rem'
              }}>
                {loading ? '...' : (dashboardData?.notes.total ?? 0)}
              </div>

              {/* Notes Breakdown */}
              {dashboardData && dashboardData.notes.total > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { label: 'Signed', value: dashboardData.notes.signed, color: '#10B981' },
                    { label: 'Final', value: dashboardData.notes.final, color: '#3B82F6' },
                    { label: 'Draft', value: dashboardData.notes.draft, color: '#F59E0B' },
                  ].filter(item => item.value > 0).map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '100%', flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                          <span style={{ fontSize: '0.8rem', color: '#374151' }}>{item.label}</span>
                          <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 600 }}>{item.value}</span>
                        </div>
                        <div style={{ width: '100%', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px' }}>
                          <div style={{
                            width: `${(item.value / dashboardData.notes.total) * 100}%`,
                            height: '100%',
                            backgroundColor: item.color,
                            borderRadius: '3px',
                          }} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Template breakdown */}
                  <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#6B7280' }}>
                    {Object.entries(dashboardData.notes.byTemplate).map(([template, count]) => (
                      <span key={template} style={{ marginRight: '0.75rem' }}>{template}: {count}</span>
                    ))}
                  </div>
                  {dashboardData.notes.aiAssisted > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#8B5CF6', marginTop: '0.25rem' }}>
                      AI-assisted: {dashboardData.notes.aiAssisted} ({Math.round((dashboardData.notes.aiAssisted / dashboardData.notes.total) * 100)}%)
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minHeight: '200px', color: '#9CA3AF', fontSize: '0.875rem'
                }}>
                  {loading ? 'Loading...' : 'No notes this period'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab Content */}
      {mainTab === 'reports' && (
        <div>
          {/* Info text */}
          <p style={{
            fontSize: '0.875rem',
            color: '#d1d5db',
            marginBottom: '1.5rem'
          }}>
            View detailed client and billing data.{' '}
            <span style={{ color: '#4A90E2' }}>
              Learn more
            </span>
          </p>

          {/* Reports Grid - 2 columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '2rem',
            marginTop: '2rem'
          }}>
            {/* Income Category */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              {/* Category Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem'
                }}>
                  💰
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0
                  }}>
                    Income
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#d1d5db',
                    margin: 0
                  }}>
                    Reports related to your practice income
                  </p>
                </div>
              </div>

              {/* Report Links */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => setCurrentReport('income-allocation')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Income allocation
                </button>
                <button
                  onClick={() => setCurrentReport('income')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Income
                </button>
                <button
                  onClick={() => setCurrentReport('referral-income')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Referral income
                </button>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textDecoration: 'none',
                  padding: '0.5rem 0'
                }}>
                  2023 Annual Financial Activity Report
                </button>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textDecoration: 'none',
                  padding: '0.5rem 0'
                }}>
                  2024 Annual Financial Activity Report
                </button>
              </div>
            </div>

            {/* Billing Category */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              {/* Category Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#dbeafe',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem'
                }}>
                  💵
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0
                  }}>
                    Billing
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#d1d5db',
                    margin: 0
                  }}>
                    Reports related to client billing and payments
                  </p>
                </div>
              </div>

              {/* Report Links */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => setCurrentReport('outstanding-balances')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Outstanding balances
                </button>
                <button
                  onClick={() => setCurrentReport('client-invoice-aging')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Client invoice aging
                </button>
                <button
                  onClick={() => setCurrentReport('invoices')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Invoices
                </button>
              </div>
            </div>

            {/* Clients & Appointments Category */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              {/* Category Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem'
                }}>
                  📅
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0
                  }}>
                    Clients & Appointments
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#d1d5db',
                    margin: 0
                  }}>
                    Reports related to appointments and client communication
                  </p>
                </div>
              </div>

              {/* Report Links */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textDecoration: 'none',
                  padding: '0.5rem 0'
                }}>
                  Client details
                </button>
                <button onClick={() => toast.info('Coming Soon', 'This report detail will be available in a future update.')} style={{
                  fontSize: '0.875rem',
                  color: '#4A90E2',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  textDecoration: 'none',
                  padding: '0.5rem 0'
                }}>
                  Client demographics
                </button>
                <button
                  onClick={() => setCurrentReport('appointment-status')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Appointment status
                </button>
                <button
                  onClick={() => setCurrentReport('attendance')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Attendance
                </button>
                <button
                  onClick={() => setCurrentReport('appointment-reminders')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Appointment reminders
                </button>
                <button
                  onClick={() => setCurrentReport('sent-emails')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Sent emails
                </button>
              </div>
            </div>

            {/* Insurance Category */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '1.5rem'
            }}>
              {/* Category Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#e0e7ff',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem'
                }}>
                  🛡️
                </div>
                <div>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#111827',
                    margin: 0
                  }}>
                    Insurance
                  </h3>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#d1d5db',
                    margin: 0
                  }}>
                    Reports related to insurance claims and payments
                  </p>
                </div>
              </div>

              {/* Report Links */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => setCurrentReport('unpaid-insurance')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Unpaid insurance appointments
                </button>
                <button
                  onClick={() => setCurrentReport('outstanding-claims')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Outstanding claims
                </button>
                <button
                  onClick={() => setCurrentReport('filed-claims')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Filed claims
                </button>
                <button
                  onClick={() => setCurrentReport('payment-reports')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Payment Reports
                </button>
                <button
                  onClick={() => setCurrentReport('insurance-status-checks')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '0.875rem',
                    color: '#4A90E2',
                    textDecoration: 'none',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit'
                  }}
                >
                  Insurance status checks
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
